import {
    defineConnectorConstellationDerivation,
    defineConnectorItemDerivation,
    toConnectorItemCollectionDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type {
    ConnectorConstellationDerivation,
    ConnectorItemCollectionDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';
import { buildLineGuideShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';

import { toNaturalDigitAngle, toPrimitiveBandPosition } from '@/adapters/primitive/_pure/derivation/constellations';
import { toNaturalRenderedDigits } from '@/adapters/primitive/_pure/derivation/itemFactories';
import {
    toNaturalAngularLabelCollectionKey,
    toNaturalAngularLabelConstellationKey,
    toNaturalAngularLabelItemKey,
    toNaturalRadialLabelCollectionKey,
    toNaturalRadialLabelConstellationKey,
    toNaturalRadialLabelItemKey,
    toNaturalBandKeyAnchorKey,
    toNaturalBandKeyGuideKey,
    toNaturalBandKeyGuideCollectionKey,
    toNaturalBandKeyGuideConstellationKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toPrimitiveBandOpacity, toPrimitiveWheelOpacity } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

const BAND_LABEL_FONT_SIZE = 0.3;

/**
 * World offset for a point at local (0,0,radius) under Euler('XYZ', [Math.PI/2, angle, 0]) — the same
 * tip+spin convention as `toPrimitiveBandRotation`. Computed analytically so the marker can sit at the
 * right spot under an identity layout rotation.
 */
function toNaturalFixedRadialOffset(radius: number, angle: number): [number, number, number] {
    return [radius * Math.sin(angle), -radius * Math.cos(angle), 0];
}

function addPositions(a: readonly [number, number, number], b: readonly [number, number, number]): [number, number, number] {
    return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function toNaturalPowerRingMidRadius(config: Config, power: number): number {
    const innerRadius = config.base.display.wheel.innerRadius + power * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap);
    return innerRadius + config.base.display.wheel.ringWidth / 2;
}

/** External labels connect to width brackets, not to individual digit slots. */
export function buildNaturalRadialBandLabelDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
): {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    constellationDerivations: EntityConstellationDerivation[];
    connectorItemCollectionDerivations: ConnectorItemCollectionDerivation[];
    connectorConstellationDerivations: ConnectorConstellationDerivation[];
} {
    const itemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const constellationDerivations: EntityConstellationDerivation[] = [];
    const connectorItemCollectionDerivations: ConnectorItemCollectionDerivation[] = [];
    const connectorConstellationDerivations: ConnectorConstellationDerivation[] = [];
    const bracketAngle = Math.PI * 0.65;
    const aboveItemStyle = config.base.display.label.aboveItemStyleOverride;

    for (const wheel of wheels) {
        if (wheel.variant !== undefined || (wheel.valueKind ?? 'natural') !== 'natural'
            || !(wheel.bandLabels?.radial ?? config.natural.display.label.radialBandLabelsVisible)) continue;

        const fontSize = aboveItemStyle?.fontSize ?? BAND_LABEL_FONT_SIZE;
        const outerRadius = toNaturalWheelIndexRadius(config, wheel);
        const powers = [...wheel.powers].sort((a, b) => a - b);
        for (const power of wheel.powers) {
            const ref = { wheelRole: wheel.role, power };
            const itemKey = toNaturalRadialLabelItemKey({ wheelRole: wheel.role, power });
            const midRadius = toNaturalPowerRingMidRadius(config, power);
            const center = toPrimitiveBandPosition(wheel, power);
            const labelPosition = addPositions(wheel.position, [
                outerRadius + fontSize * 8,
                (powers.indexOf(power) - (powers.length - 1) / 2) * fontSize * 3.5,
                0,
            ]);
            const opacity = config.natural.display.label.radialBandLabelOpacity * toPrimitiveBandOpacity(wheel, power);
            const color = aboveItemStyle?.color ?? config.base.display.label.digitLabelColor;
            const radial = (radius: number) => addPositions(center, toNaturalFixedRadialOffset(radius, bracketAngle));
            const cap = (position: [number, number, number], direction: number) => addPositions(position,
                [Math.cos(bracketAngle) * fontSize * direction / 3, Math.sin(bracketAngle) * fontSize * direction / 3, 0]);
            const inner = radial(midRadius - config.base.display.wheel.ringWidth / 2);
            const outer = radial(midRadius + config.base.display.wheel.ringWidth / 2);
            const points = {
                inner, outer, middle: radial(midRadius),
                innerCapA: cap(inner, -1), innerCapB: cap(inner, 1),
                outerCapA: cap(outer, -1), outerCapB: cap(outer, 1),
                elbow: addPositions(wheel.position, [outerRadius + fontSize, labelPosition[1] - wheel.position[1], 0]),
                end: addPositions(labelPosition, [-fontSize * 4.5, 0, 0]),
            };
            const anchors = Object.entries(points).map(([point, position]) => {
                const key = toNaturalBandKeyAnchorKey({ ...ref, point });
                constellationDerivations.push(defineEntityConstellationDerivation({
                    constellation: {
                        constellationKind: 'entity',
                        constellationKey: `${toNaturalRadialLabelConstellationKey(ref)}:${point}`,
                        itemCollectionKey: `${toNaturalRadialLabelCollectionKey(ref)}:${point}`,
                        anchor: { itemKey: key, anchorKey: 'center' },
                        meta: { wheelRole: wheel.role, power },
                    },
                    parametricState: { layout: { layoutKind: 'sector-ring',
                        anchor: { anchorKind: 'position', position }, innerRadius: 0, outerRadius: 0.01,
                        angularGapFraction: 0, arcSegments: config.base.display.wheel.arcSegments, rotation: [0, 0, 0] } },
                }));
                return toEntityItemCollectionDerivation({
                    itemCollectionKey: `${toNaturalRadialLabelCollectionKey(ref)}:${point}`,
                    itemDerivations: [defineEntityItemDerivation({
                        item: { itemKind: 'entity', itemKey: key, name: `Band key ${point}` },
                        parametricState: { shape: buildSphereShape({ radius: 0.01 }), visualStyle: { opacity: 0 } },
                    })],
                });
            });
            itemCollectionDerivations.push(...anchors);
            const segments = [
                ['width', 'inner', 'outer'], ['inner-cap', 'innerCapA', 'innerCapB'],
                ['outer-cap', 'outerCapA', 'outerCapB'], ['leader', 'middle', 'elbow'], ['label', 'elbow', 'end'],
            ] as const;
            connectorItemCollectionDerivations.push(toConnectorItemCollectionDerivation({
                itemCollectionKey: toNaturalBandKeyGuideCollectionKey(ref),
                itemDerivations: segments.map(([part, from, to]) => defineConnectorItemDerivation({
                    item: { itemKind: 'connector', itemKey: toNaturalBandKeyGuideKey({ ...ref, part }),
                        shape: buildLineGuideShape(),
                        endpoints: {
                            from: { endpointKind: 'static', itemKey: toNaturalBandKeyAnchorKey({ ...ref, point: from }), anchorKey: 'center' },
                            to: { endpointKind: 'static', itemKey: toNaturalBandKeyAnchorKey({ ...ref, point: to }), anchorKey: 'center' },
                        } },
                    parametricState: { visualStyle: { color, opacity }, depthTest: false,
                        ...(part === 'label' || !wheel.bandLabels?.sweep?.powers.includes(power) ? {} : { endpointOrbit: {
                            center, plane: 'xy', durationSeconds: wheel.bandLabels.sweep.durationMs / 1000,
                            afterCue: wheel.bandLabels.sweep.afterCue, endpoints: part === 'leader' ? 'from' : 'both',
                        } }),
                    },
                })),
            }));
            connectorConstellationDerivations.push(defineConnectorConstellationDerivation({
                constellation: { constellationKind: 'connector', constellationKey: toNaturalBandKeyGuideConstellationKey(ref),
                    itemCollectionKey: toNaturalBandKeyGuideCollectionKey(ref) },
                parametricState: { visibility: { visible: true } },
            }));

            itemCollectionDerivations.push(
                toEntityItemCollectionDerivation({
                    itemCollectionKey: toNaturalRadialLabelCollectionKey({ wheelRole: wheel.role, power }),
                    itemDerivations: [
                        defineEntityItemDerivation({
                            item: {
                                itemKind: 'entity',
                                itemKey,
                                name: `${wheel.role} 10^${power} radial label`,
                                description: `Radial band label for the ${wheel.role} wheel at 10^${power}.`,
                            },
                            parametricState: {
                                visualStyle: {
                                    opacity,
                                },
                                label: {
                                    aboveItem: {
                                        ...(aboveItemStyle ?? {}),
                                        text: `${['Ones', 'Tens', 'Hundreds'][power]} \u00b7 10^${power}`,
                                        color: aboveItemStyle?.color ?? config.base.display.label.digitLabelColor,
                                        fontSize: aboveItemStyle?.fontSize ?? BAND_LABEL_FONT_SIZE,
                                        placement: { placementKind: 'item-origin' },
                                        orientation: { orientationKind: 'billboard' },
                                        depthTest: false,
                                        renderOrder: 100,
                                    },
                                },
                            },
                        }),
                    ],
                }),
            );

            constellationDerivations.push(
                defineEntityConstellationDerivation({
                    constellation: {
                        constellationKind: 'entity',
                        constellationKey: toNaturalRadialLabelConstellationKey({ wheelRole: wheel.role, power }),
                        itemCollectionKey: toNaturalRadialLabelCollectionKey({ wheelRole: wheel.role, power }),
                        anchor: { itemKey, anchorKey: 'center' },
                        meta: { wheelRole: wheel.role, power },
                    },
                    parametricState: {
                        layout: {
                            layoutKind: 'sector-ring',
                            anchor: {
                                anchorKind: 'position',
                                position: labelPosition,
                            },
                            innerRadius: 0,
                            outerRadius: 0.01,
                            angularGapFraction: 0,
                            arcSegments: config.base.display.wheel.arcSegments,
                            rotation: [0, 0, 0],
                        },
                    },
                }),
            );
        }
    }

    return { itemCollectionDerivations, constellationDerivations, connectorItemCollectionDerivations, connectorConstellationDerivations };
}

export function toNaturalWheelIndexRadius(config: Config, wheel: PrimitiveWheelState): number {
    const maxPower = wheel.powers.length > 0 ? Math.max(...wheel.powers) : 0;
    const outerRadius = config.base.display.wheel.innerRadius + maxPower * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap) + config.base.display.wheel.ringWidth;
    const fontSize = config.base.display.label.aboveItemStyleOverride?.fontSize ?? BAND_LABEL_FONT_SIZE;
    return outerRadius + Math.max(config.natural.display.wheel.ringGap / 2, fontSize);
}

/** One marker per digit slot (10 total), just outside the outermost ring, independent of any power's rotation. */
export function buildNaturalAngularBandLabelDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
): {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    constellationDerivations: EntityConstellationDerivation[];
} {
    const itemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const constellationDerivations: EntityConstellationDerivation[] = [];
    const renderedDigits = toNaturalRenderedDigits(config);

    const aboveItemStyle = config.base.display.label.aboveItemStyleOverride;

    for (const wheel of wheels) {
        if (wheel.variant !== undefined || (wheel.valueKind ?? 'natural') !== 'natural'
            || !(wheel.bandLabels?.angular ?? config.natural.display.label.angularBandLabelsVisible)) continue;

        const radius = toNaturalWheelIndexRadius(config, wheel);

        for (const digit of renderedDigits) {
            const itemKey = toNaturalAngularLabelItemKey({ wheelRole: wheel.role, digit });
            const angle = toNaturalDigitAngle(config, digit);

            itemCollectionDerivations.push(
                toEntityItemCollectionDerivation({
                    itemCollectionKey: toNaturalAngularLabelCollectionKey({ wheelRole: wheel.role, digit }),
                    itemDerivations: [
                        defineEntityItemDerivation({
                            item: {
                                itemKind: 'entity',
                                itemKey,
                                name: `${wheel.role} digit ${digit} angular label`,
                                description: `Angular slot label for the ${wheel.role} wheel at digit ${digit}.`,
                            },
                            parametricState: {
                                visualStyle: {
                                    opacity: config.natural.display.label.angularBandLabelOpacity * toPrimitiveWheelOpacity(wheel),
                                },
                                label: {
                                    aboveItem: {
                                        ...(aboveItemStyle ?? {}),
                                        text: String(digit),
                                        color: aboveItemStyle?.color ?? config.base.display.label.digitLabelColor,
                                        fontSize: aboveItemStyle?.fontSize ?? BAND_LABEL_FONT_SIZE,
                                        placement: { placementKind: 'item-origin' },
                                        orientation: { orientationKind: 'billboard' },
                                    },
                                },
                            },
                        }),
                    ],
                }),
            );

            constellationDerivations.push(
                defineEntityConstellationDerivation({
                    constellation: {
                        constellationKind: 'entity',
                        constellationKey: toNaturalAngularLabelConstellationKey({ wheelRole: wheel.role, digit }),
                        itemCollectionKey: toNaturalAngularLabelCollectionKey({ wheelRole: wheel.role, digit }),
                        anchor: { itemKey, anchorKey: 'center' },
                        meta: { wheelRole: wheel.role, digit },
                    },
                    parametricState: {
                        layout: {
                            layoutKind: 'sector-ring',
                            anchor: {
                                anchorKind: 'position',
                                position: addPositions(wheel.position, toNaturalFixedRadialOffset(radius, angle)),
                            },
                            innerRadius: 0,
                            outerRadius: 0.01,
                            angularGapFraction: 0,
                            arcSegments: config.base.display.wheel.arcSegments,
                            rotation: [0, 0, 0],
                        },
                    },
                }),
            );
        }
    }

    return { itemCollectionDerivations, constellationDerivations };
}
